import { Button, Modal } from "@arco-design/web-react";
import { Pencil, Trash2 } from "lucide-react";
import {
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { createUuid } from "../../../lib/trace/context";
import {
  changeDayCount,
  formatDayWeekday,
  removeDay,
  reorderDay,
  resolveDayEndpointNodes,
  requiresRouteConfirmation,
  summarizeDayEndpoints,
} from "../service";
import type {
  DayCountChange,
  ItineraryEditor,
  ItineraryNode,
  PointCandidateResult,
  PointCandidateQuery,
  NodeDraft,
  TimeSlot,
} from "../types";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";
import { ItineraryPointDialog, type PointNodeCandidateKind } from "./ItineraryPointDialog";
import { resolvePointRecommendationAnchor } from "../service/itineraryCandidateRecommendationService";

type Props = {
  editor: ItineraryEditor;
  selectedDayId: string;
  readOnly?: boolean;
  onChange: (editor: ItineraryEditor) => void;
  onSelectDay: (dayId: string) => void;
  onAnnounce: (message: string) => void;
  listPointCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
};

const createId = createUuid;
const stayAreaOnly: readonly PointNodeCandidateKind[] = ["stay_area"];
const getStayAreaOnly = () => stayAreaOnly;

type DayDropTarget = {
  dayId: string;
  placement: "before" | "after";
};

type DayPointerDrag = {
  dayId: string;
  pointerId: number;
  active: boolean;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  nativeDragging: boolean;
};

/** 行程天数与日期导航。 */
export function ItineraryDaySidebar({
  editor,
  selectedDayId,
  readOnly = false,
  onChange,
  onSelectDay,
  onAnnounce,
  listPointCandidates,
}: Props) {
  const [targetCount, setTargetCount] = useState(String(editor.days.length));
  const [countError, setCountError] = useState("");
  const [pendingChange, setPendingChange] = useState<{
    change: DayCountChange;
    count: number;
  } | null>(null);
  const [pendingDeleteDayId, setPendingDeleteDayId] = useState<string | null>(null);
  const [editingStayDayId, setEditingStayDayId] = useState<string | null>(null);
  const [draggedDayId, setDraggedDayId] = useState("");
  const [dropTarget, setDropTarget] = useState<DayDropTarget | null>(null);
  const pointerDragRef = useRef<DayPointerDrag | null>(null);
  const listDayCandidates = useCallback((query: PointCandidateQuery, signal?: AbortSignal) => {
    if (!listPointCandidates) throw new Error("点位候选暂不可用");
    return listPointCandidates({ ...query, includeTrainOvernight: true }, signal);
  }, [listPointCandidates]);

  useEffect(() => {
    setTargetCount(String(editor.days.length));
  }, [editor.days.length]);

  const commitChange = (change: DayCountChange, count: number) => {
    setCountError("");
    onChange(change.next);
    setTargetCount(String(count));
    onSelectDay(change.next.days[Math.min(count - 1, change.next.days.length - 1)]?.id ?? "");
    onAnnounce(`行程已调整为${count}天`);
  };

  const applyCount = (value: string) => {
    const count = Number(value);
    try {
      const change = changeDayCount(editor, count, createId);
      if (change.requiresConfirmation) {
        setPendingChange({ change, count });
      } else commitChange(change, count);
    } catch (error) {
      const message = error instanceof Error ? error.message : "天数调整失败";
      setTargetCount(String(editor.days.length));
      setCountError(message);
      onAnnounce(message);
    }
  };
  const arrangedDays = editor.days.filter((day) => day.nodes.length > 0).length;
  const pendingDeleteDay = editor.days.find((day) => day.id === pendingDeleteDayId) ?? null;
  const editingStayDay = editor.days.find((day) => day.id === editingStayDayId) ?? null;
  const editingStayNode: ItineraryNode | null = editingStayDay
    ? {
        id: `day-stay-${editingStayDay.id}`,
        timeSlot: "evening",
        sortOrder: 1,
        nodeType: "stay_area",
        stayAreaId: editingStayDay.stayAreaId ?? null,
        title: editingStayDay.stayAreaName ?? "",
      }
    : null;

  const submitStayArea = (draft: NodeDraft & { timeSlot: TimeSlot }) => {
    if (!editingStayDay || !draft.stayAreaId) return;
    const dayId = editingStayDay.id;
    onChange({
      ...editor,
      days: editor.days.map((day) => day.id === dayId
        ? { ...day, stayAreaId: draft.stayAreaId, stayAreaName: draft.title.trim() }
        : day),
    });
    onAnnounce(`第${editingStayDay.dayNumber}天考察点已更新为${draft.title.trim()}`);
    setEditingStayDayId(null);
  };

  const applyReorder = (next: ItineraryEditor, movedDayId: string) => {
    const movedDay = next.days.find((day) => day.id === movedDayId);
    onChange(next);
    onAnnounce(movedDay ? `已移动到第${movedDay.dayNumber}天` : "行程天次已调整");
  };

  const requestReorder = (
    sourceDayId: string,
    targetDayId: string,
    placement: DayDropTarget["placement"],
  ) => {
    if (!sourceDayId || sourceDayId === targetDayId) return;
    const sourceIndex = editor.days.findIndex((day) => day.id === sourceDayId);
    const targetIndex = editor.days.findIndex((day) => day.id === targetDayId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    if (
      (placement === "before" && sourceIndex + 1 === targetIndex)
      || (placement === "after" && sourceIndex - 1 === targetIndex)
    ) return;
    const next = reorderDay(editor, sourceDayId, targetDayId, placement);
    applyReorder(next, sourceDayId);
  };

  const clearDragFeedback = () => {
    setDraggedDayId("");
    setDropTarget(null);
  };

  const resolveDropTarget = (
    sourceDayId: string,
    clientX: number,
    clientY: number,
  ): DayDropTarget | null => {
    const element = document.elementFromPoint?.(clientX, clientY);
    if (!(element instanceof Element)) return null;
    const target = element.closest<HTMLElement>("[data-itinerary-day-id]");
    const targetDayId = target?.dataset.itineraryDayId;
    if (!target || !targetDayId || targetDayId === sourceDayId) return null;
    const bounds = target.getBoundingClientRect();
    return {
      dayId: targetDayId,
      placement: clientY < bounds.top + bounds.height / 2 ? "before" : "after",
    };
  };

  const updatePointerDragTarget = (clientX: number, clientY: number) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag) return;
    pointerDrag.lastX = clientX;
    pointerDrag.lastY = clientY;
    if (Math.hypot(clientX - pointerDrag.startX, clientY - pointerDrag.startY) < 8) return;
    if (!pointerDrag.active) {
      pointerDrag.active = true;
      const source = document.querySelector<HTMLElement>(`[data-itinerary-day-id="${pointerDrag.dayId}"]`);
      source?.setPointerCapture?.(pointerDrag.pointerId);
      setDraggedDayId(pointerDrag.dayId);
    }
    setDropTarget(resolveDropTarget(pointerDrag.dayId, clientX, clientY));
  };

  const finishPointerMove = (clientX: number, clientY: number) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag) return;
    pointerDragRef.current = null;
    clearDragFeedback();
    const missingNativeCoordinates = pointerDrag.nativeDragging && clientX === 0 && clientY === 0;
    const resolvedX = missingNativeCoordinates ? pointerDrag.lastX : clientX;
    const resolvedY = missingNativeCoordinates ? pointerDrag.lastY : clientY;
    if (!pointerDrag.active && !pointerDrag.nativeDragging) {
      return;
    }
    if (Math.hypot(
      resolvedX - pointerDrag.startX,
      resolvedY - pointerDrag.startY,
    ) < 8) return;
    const target = resolveDropTarget(pointerDrag.dayId, resolvedX, resolvedY);
    if (target) requestReorder(pointerDrag.dayId, target.dayId, target.placement);
  };

  const beginPointerDrag = (event: PointerEvent<HTMLLIElement>, dayId: string) => {
    if (readOnly || event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(".itinerary-day-delete, .itinerary-day-stay-edit")) return;
    pointerDragRef.current = {
      dayId,
      pointerId: event.pointerId,
      active: false,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      nativeDragging: false,
    };
    setDropTarget(null);
  };

  const finishPointerDrag = (event: PointerEvent<HTMLLIElement>) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag || pointerDrag.pointerId !== event.pointerId) return;
    if (pointerDrag.active) event.currentTarget.releasePointerCapture?.(event.pointerId);
    finishPointerMove(event.clientX, event.clientY);
  };

  const handleNativeDragTarget = (event: DragEvent<HTMLLIElement>, targetDayId: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const sourceDayId = event.dataTransfer.getData("text/plain") || draggedDayId;
    if (!sourceDayId || sourceDayId === targetDayId) {
      setDropTarget(null);
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    setDropTarget({
      dayId: targetDayId,
      placement: event.clientY < bounds.top + bounds.height / 2 ? "before" : "after",
    });
  };

  const handleReorderKeyDown = (event: KeyboardEvent<HTMLButtonElement>, dayId: string) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    const index = editor.days.findIndex((day) => day.id === dayId);
    const target = editor.days[index + (event.key === "ArrowUp" ? -1 : 1)];
    if (!target) return;
    event.preventDefault();
    requestReorder(dayId, target.id, event.key === "ArrowUp" ? "before" : "after");
  };

  return (
    <nav className="itinerary-day-sidebar" aria-label="行程日期">
      <div className="itinerary-day-sidebar-head">
        <div className="itinerary-day-sidebar-title">
          {!readOnly ? <label htmlFor="itinerary-day-count">行程天数</label> : null}
          <span>已安排 {arrangedDays} / {editor.days.length} 天</span>
        </div>
        {!readOnly ? <div className="itinerary-day-count">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              applyCount(targetCount);
            }}
          >
            <div className="itinerary-day-count-field">
              <input
                type="number"
                id="itinerary-day-count"
                min={1}
                max={30}
                inputMode="numeric"
                value={targetCount}
                aria-invalid={countError ? "true" : undefined}
                aria-describedby={countError ? "itinerary-day-count-error" : undefined}
                onChange={(event) => {
                  setCountError("");
                  setTargetCount(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  applyCount(targetCount);
                }}
              />
              <span aria-hidden="true">天</span>
            </div>
            <Button htmlType="button" size="small" type="primary" onClick={() => applyCount(targetCount)}>
              应用
            </Button>
          </form>
          {countError ? (
            <span id="itinerary-day-count-error" className="itinerary-day-count-error" role="alert">
              {countError}
            </span>
          ) : null}
        </div> : null}
      </div>
      <ol className="itinerary-day-list">
        {editor.days.map((day) => {
          const weekday = formatDayWeekday(editor.startDate, day.dayNumber);
          const endpointSummary = summarizeDayEndpoints(resolveDayEndpointNodes(day));
          const dayLocationLabel = day.stayAreaName?.trim() || endpointSummary?.label || "暂无点位";
          const pendingRouteCount = editor.segments.filter(
            (segment) => segment.startDayId === day.id && requiresRouteConfirmation(segment),
          ).length;
          const state = day.nodes.length === 0
            ? { className: "is-empty", label: "未安排" }
            : pendingRouteCount > 0
              ? { className: "is-partial", label: `${pendingRouteCount} 条路线待确认` }
              : { className: "is-done", label: "已安排" };
          const sortable = !readOnly && editor.days.length > 1;
          const dropClass = dropTarget?.dayId === day.id
            ? ` is-drop-${dropTarget.placement}`
            : "";
          return (
            <li
              key={day.id}
              data-itinerary-day-id={day.id}
              className={`${sortable ? "is-sortable" : ""}${draggedDayId === day.id ? " is-dragging" : ""}${dropClass}`}
              draggable={sortable}
              onPointerDown={(event) => beginPointerDrag(event, day.id)}
              onPointerMove={(event) => {
                if (pointerDragRef.current?.pointerId !== event.pointerId) return;
                updatePointerDragTarget(event.clientX, event.clientY);
              }}
              onPointerUp={finishPointerDrag}
              onPointerCancel={() => {
                if (!pointerDragRef.current?.nativeDragging) {
                  pointerDragRef.current = null;
                  clearDragFeedback();
                }
              }}
              onDragStart={(event) => {
                if (!sortable || (event.target instanceof Element && event.target.closest(".itinerary-day-delete, .itinerary-day-stay-edit"))) {
                  event.preventDefault();
                  return;
                }
                if (pointerDragRef.current?.dayId === day.id) {
                  pointerDragRef.current.nativeDragging = true;
                  pointerDragRef.current.active = true;
                }
                setDraggedDayId(day.id);
                setDropTarget(null);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", day.id);
              }}
              onDrag={(event) => {
                if (pointerDragRef.current?.dayId === day.id && (event.clientX || event.clientY)) {
                  pointerDragRef.current.lastX = event.clientX;
                  pointerDragRef.current.lastY = event.clientY;
                }
              }}
              onDragEnd={(event) => {
                if (pointerDragRef.current) finishPointerMove(event.clientX, event.clientY);
                else clearDragFeedback();
              }}
              onDragEnter={(event) => handleNativeDragTarget(event, day.id)}
              onDragOver={(event) => handleNativeDragTarget(event, day.id)}
              onDragLeave={(event) => {
                if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
                setDropTarget((current) => current?.dayId === day.id ? null : current);
              }}
              onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const sourceDayId = event.dataTransfer.getData("text/plain") || draggedDayId;
                const bounds = event.currentTarget.getBoundingClientRect();
                const placement = dropTarget?.dayId === day.id
                  ? dropTarget.placement
                  : event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
                pointerDragRef.current = null;
                clearDragFeedback();
                requestReorder(sourceDayId, day.id, placement);
              }}
            >
              <button
                type="button"
                className={`itinerary-day-select${sortable ? " is-sortable" : ""}${day.id === selectedDayId ? " is-active" : ""}`}
                aria-current={day.id === selectedDayId ? "page" : undefined}
                aria-label={`D${day.dayNumber}${day.dayTitle?.trim() || "待定"} 第 ${day.dayNumber} 天 ${weekday} ${day.dayDate || "日期待定"} ${endpointSummary?.label || "暂无点位"}`}
                aria-keyshortcuts={sortable ? "ArrowUp ArrowDown" : undefined}
                title={sortable ? "点击切换编辑日；按上下方向键调整顺序" : undefined}
                onClick={() => onSelectDay(day.id)}
                onKeyDown={sortable ? (event) => handleReorderKeyDown(event, day.id) : undefined}
              >
                <span className="itinerary-day-badge">D{day.dayNumber}</span>
                <span className="itinerary-day-copy">
                  <strong>{day.dayTitle?.trim() || "待定"}</strong>
                  <small title={dayLocationLabel}>{weekday ? `${weekday} ` : ""}{day.dayDate || "日期待定"} · {dayLocationLabel}</small>
                </span>
                <span
                  className={`itinerary-day-state ${state.className}`}
                  aria-label={`状态：${state.label}`}
                  title={state.label}
                />
              </button>
              {!readOnly && listPointCandidates ? (
                <Button
                  className="itinerary-day-stay-edit"
                  type="text"
                  size="mini"
                  icon={<Pencil size={14} />}
                  aria-label={`编辑第${day.dayNumber}天住宿点`}
                  title="编辑住宿点"
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditingStayDayId(day.id);
                  }}
                />
              ) : null}
              {!readOnly && editor.days.length > 1 ? (
                <Button
                  className="itinerary-day-delete"
                  type="text"
                  size="mini"
                  icon={<Trash2 size={14} />}
                  aria-label={`移除行程日 ${day.dayDate || day.dayNumber}`}
                  title={`删除第 ${day.dayNumber} 天`}
                  onClick={() => setPendingDeleteDayId(day.id)}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
      <Modal
        visible={Boolean(pendingChange)}
        title="缩短行程"
        closeIcon={<ItineraryDialogCloseButton label="关闭缩短行程" />}
        okText="确认缩短"
        cancelText="取消"
        onOk={() => {
          if (pendingChange) commitChange(pendingChange.change, pendingChange.count);
          setPendingChange(null);
        }}
        onCancel={() => {
          setTargetCount(String(editor.days.length));
          setPendingChange(null);
        }}
      >
        将删除后 {pendingChange ? editor.days.length - pendingChange.count : 0} 天及其点位和路线，是否继续？
      </Modal>
      <Modal
        visible={Boolean(pendingDeleteDay)}
        title="删除行程日"
        closeIcon={<ItineraryDialogCloseButton label="关闭删除行程日" />}
        okText="确认删除"
        cancelText="取消"
        onOk={() => {
          if (!pendingDeleteDay) return;
          try {
            const removedIndex = editor.days.findIndex((day) => day.id === pendingDeleteDay.id);
            const next = removeDay(editor, pendingDeleteDay.id, createId);
            onChange(next);
            if (selectedDayId === pendingDeleteDay.id) {
              onSelectDay(next.days[Math.min(removedIndex, next.days.length - 1)]?.id ?? "");
            }
            onAnnounce(`已删除第${pendingDeleteDay.dayNumber}天，后续天次已顺延`);
          } catch (error) {
            const message = error instanceof Error ? error.message : "删除行程日失败";
            setCountError(message);
            onAnnounce(message);
          }
          setPendingDeleteDayId(null);
        }}
        onCancel={() => setPendingDeleteDayId(null)}
      >
        将只删除第 {pendingDeleteDay?.dayNumber ?? ""} 天“{pendingDeleteDay?.dayTitle?.trim() || "待定"}”及其点位、路线和轨道；后续行程日会自动顺延，是否继续？
      </Modal>
      <ItineraryPointDialog
        visible={Boolean(editingStayDay)}
        title={editingStayDay ? `编辑第${editingStayDay.dayNumber}天住宿点` : "编辑住宿点"}
        initial={editingStayNode}
        defaultSlot="evening"
        lockSlot
        selectionOnly
        candidateLabel="住宿点"
        onCancel={() => setEditingStayDayId(null)}
        onSubmit={submitStayArea}
        listCandidates={listPointCandidates ? listDayCandidates : undefined}
        getRecommendationAnchor={(slot) => {
          if (!editingStayDay) return null;
          const last = resolveDayEndpointNodes(editingStayDay).at(-1);
          return resolvePointRecommendationAnchor(editor, {
            dayId: editingStayDay.id, timeSlot: slot, dayEnd: true,
            ...(last?.nodeType === "stay_area" ? { beforeNodeId: last.id } : {}),
          });
        }}
        getAllowedKinds={getStayAreaOnly}
      />
    </nav>
  );
}
